import {
    MOBILE_BILLING_REASONS,
    REVENUECAT_ANDROID_API_KEY,
    REVENUECAT_IOS_API_KEY
} from '../constants/mobileBilling';

const loadRevenueCatModuleDefault = () => import('@revenuecat/purchases-capacitor');

const getDefaultCapacitorRuntime = () => {
    if (typeof window === 'undefined') {
        return undefined;
    }

    return window.Capacitor;
};

const getErrorMessage = (error) => (
    error?.message || error?.errMsg?.message || error?.errMsg || 'Unable to configure RevenueCat'
);

export const isNativeCapacitorRuntime = (capacitorRuntime = getDefaultCapacitorRuntime()) => (
    Boolean(capacitorRuntime?.isNativePlatform?.())
);

export const getRevenueCatApiKeyForPlatform = (platform) => {
    if (platform === 'ios') {
        return REVENUECAT_IOS_API_KEY;
    }

    if (platform === 'android') {
        return REVENUECAT_ANDROID_API_KEY;
    }

    return '';
};

export const configureRevenueCat = async ({
    appUserID,
    capacitorRuntime = getDefaultCapacitorRuntime(),
    loadRevenueCatModule = loadRevenueCatModuleDefault
} = {}) => {
    if (!isNativeCapacitorRuntime(capacitorRuntime)) {
        return {
            configured: false,
            reason: MOBILE_BILLING_REASONS.NOT_NATIVE_RUNTIME
        };
    }

    const platform = capacitorRuntime?.getPlatform?.();

    if (platform !== 'ios' && platform !== 'android') {
        return {
            configured: false,
            platform,
            reason: MOBILE_BILLING_REASONS.UNSUPPORTED_PLATFORM
        };
    }

    const apiKey = getRevenueCatApiKeyForPlatform(platform);

    if (!apiKey) {
        return {
            configured: false,
            platform,
            reason: MOBILE_BILLING_REASONS.MISSING_REVENUECAT_API_KEY
        };
    }

    try {
        const { LOG_LEVEL, Purchases } = await loadRevenueCatModule();

        if (LOG_LEVEL?.DEBUG && process.env.NODE_ENV !== 'production') {
            await Purchases.setLogLevel?.({ level: LOG_LEVEL.DEBUG });
        }

        const configuration = { apiKey };

        if (appUserID) {
            configuration.appUserID = String(appUserID);
        }

        await Purchases.configure(configuration);

        return {
            configured: true,
            platform,
            appUserID: configuration.appUserID
        };
    } catch (error) {
        return {
            configured: false,
            platform,
            reason: MOBILE_BILLING_REASONS.REVENUECAT_CONFIGURE_FAILED,
            error: getErrorMessage(error)
        };
    }
};

export const getRevenueCatOfferings = async ({
    loadRevenueCatModule = loadRevenueCatModuleDefault
} = {}) => {
    const { Purchases } = await loadRevenueCatModule();
    return Purchases.getOfferings();
};

const getAvailablePackages = (offering) => (
    Array.isArray(offering?.availablePackages) ? offering.availablePackages : []
);

export const findRevenueCatPackageByProductId = (offerings, productId) => {
    const allOfferings = Object.values(offerings?.all || {});
    const packages = [
        ...getAvailablePackages(offerings?.current),
        ...allOfferings.flatMap(getAvailablePackages)
    ];

    return packages.find((item) => item?.product?.identifier === productId);
};

export const purchaseRevenueCatPackage = async (
    revenueCatPackage,
    { loadRevenueCatModule = loadRevenueCatModuleDefault } = {}
) => {
    const { Purchases } = await loadRevenueCatModule();
    return Purchases.purchasePackage({ aPackage: revenueCatPackage });
};

export const restoreRevenueCatPurchases = async ({
    loadRevenueCatModule = loadRevenueCatModuleDefault
} = {}) => {
    const { Purchases } = await loadRevenueCatModule();
    return Purchases.restorePurchases();
};
